import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack,
  TextField, Autocomplete, ListSubheader, Chip, Box, Typography, Alert,
} from '@mui/material';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { getFabricUsage } from '../api';

const roundQty = (q) => Math.round((Number(q) || 0) * 100) / 100;

const EMPTY_FORM = {
  number: '', model_id: '', fabric_id: '', fabric_name: '',
  fabric_id_2: '', fabric_name_2: '', category: 'waiting',
  fabric_status: 'not_available', price_type: 'standard', custom_price: '',
  assignment: '', destination: '', notes: '', delivery_date: '',
};

/**
 * Изолированный диалог формы дивана: всё состояние локальное,
 * родительская страница не ре-рендерится при наборе текста.
 */
function SofaFormDialog({
  open,
  mode = 'add',
  title,
  initialData,
  models,
  fabrics,
  sofas,
  onClose,
  onSubmit,
  readOnly = false,
}) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [expandedModelCats, setExpandedModelCats] = useState({});
  const [fabricUsage, setFabricUsage] = useState(null);
  const [fabricUsage2, setFabricUsage2] = useState(null);

  // Синхронизация при открытии
  useEffect(() => {
    if (open) {
      setFormData({ ...EMPTY_FORM, ...(initialData || {}) });
      setFabricUsage(null);
      setFabricUsage2(null);
    }
  }, [open, initialData]);

  const change = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'fabric_id') {
      if (value) {
        getFabricUsage(parseInt(value)).then(r => setFabricUsage(r.data)).catch(() => setFabricUsage(null));
      } else setFabricUsage(null);
    }
    if (field === 'fabric_id_2') {
      if (value) {
        getFabricUsage(parseInt(value)).then(r => setFabricUsage2(r.data)).catch(() => setFabricUsage2(null));
      } else setFabricUsage2(null);
    }
  };

  const handleSubmit = () => {
    if (!formData.number || !formData.model_id) {
      alert('Заполните номер и модель');
      return;
    }
    onSubmit(formData);
  };

  const renderFabricAutocomplete = (idField, nameField, label) => (
    <Autocomplete
      readOnly={readOnly}
      freeSolo
      options={fabrics}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option ? `${option.name} — ${roundQty(option.quantity) > 0 ? `${roundQty(option.quantity)} ${option.unit || 'м'}` : 'нет в наличии'}` : '';
      }}
      value={fabrics.find(f => f.id === parseInt(formData[idField])) || formData[nameField] || null}
      onChange={(_, newValue) => {
        if (typeof newValue === 'string') {
          change(idField, '');
          change(nameField, newValue);
        } else if (newValue) {
          change(idField, newValue.id);
          change(nameField, '');
        } else {
          change(idField, '');
          change(nameField, '');
        }
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input') change(nameField, newInputValue);
      }}
      renderInput={(params) => <TextField {...params} label={label} placeholder="Поиск или введите название..." />}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            <span>{option.name}</span>
            <Chip
              label={roundQty(option.quantity) > 0 ? `${roundQty(option.quantity)} ${option.unit || 'м'}` : 'нет'}
              size="small"
              color={option.quantity > 0 ? 'success' : 'error'}
              variant="outlined"
              sx={{ ml: 1 }}
            />
          </Box>
        </li>
      )}
      filterOptions={(options, { inputValue }) => options.filter(o => o.name.toLowerCase().includes(inputValue.toLowerCase()))}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      noOptionsText="Ткань не найдена — можно ввести вручную"
      fullWidth
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth keepMounted={false}>
      <DialogTitle>{title || (mode === 'add' ? 'Добавить диван' : 'Редактировать диван')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Номер"
            value={formData.number}
            onChange={(e) => change('number', e.target.value)}
            required
            fullWidth
            InputProps={{ readOnly }}
          />
          <Autocomplete
            readOnly={readOnly}
            options={[...models].sort((a, b) => (a.category || '').localeCompare(b.category || ''))}
            groupBy={(option) => option.category || 'Без категории'}
            getOptionLabel={(option) => option.name || ''}
            value={models.find(m => m.id === formData.model_id) || null}
            onChange={(_, newValue) => change('model_id', newValue ? newValue.id : '')}
            renderInput={(params) => <TextField {...params} label="Модель" required />}
            renderGroup={(params) => (
              <li key={params.key}>
                <ListSubheader
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, bgcolor: 'grey.50', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setExpandedModelCats(prev => ({ ...prev, [params.group]: !prev[params.group] }))}
                >
                  {expandedModelCats[params.group] ? <FolderOpenIcon fontSize="small" color="action" /> : <FolderIcon fontSize="small" color="action" />}
                  {params.group}
                  <Chip label={React.Children.count(params.children)} size="small" sx={{ ml: 'auto', height: 20, fontSize: 11 }} />
                  <ExpandMoreIcon fontSize="small" sx={{ transform: expandedModelCats[params.group] ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
                </ListSubheader>
                {expandedModelCats[params.group] && <ul style={{ padding: 0 }}>{params.children}</ul>}
              </li>
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="Модели не найдены"
            fullWidth
          />
          {renderFabricAutocomplete('fabric_id', 'fabric_name', 'Ткань')}
          {formData.fabric_id && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
              <Typography variant="body2" color="text.secondary">Наличие ткани:</Typography>
              {(() => {
                const selectedFabric = fabrics.find(f => f.id === parseInt(formData.fabric_id));
                if (!selectedFabric) return <Chip label="Не найдена" size="small" color="default" />;
                return selectedFabric.quantity > 0
                  ? <Chip label={`В наличии: ${roundQty(selectedFabric.quantity)} ${selectedFabric.unit || 'м'}`} size="small" color="success" />
                  : <Chip label="Надо заказать" size="small" color="error" />;
              })()}
            </Box>
          )}
          {fabricUsage && fabricUsage.sofas && fabricUsage.sofas.length > 0 && (
            <Alert severity="info" sx={{ py: 0.5, borderRadius: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Ткань используется в {fabricUsage.sofas.length} диван(ах):
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                {fabricUsage.sofas.slice(0, 5).map(s => `#${s.sofa_number} (${s.model_name || '—'})`).join(', ')}
                {fabricUsage.sofas.length > 5 ? ` и ещё ${fabricUsage.sofas.length - 5}...` : ''}
              </Typography>
            </Alert>
          )}
          {renderFabricAutocomplete('fabric_id_2', 'fabric_name_2', 'Ткань 2 (вторая)')}
          {fabricUsage2 && fabricUsage2.sofas && fabricUsage2.sofas.length > 0 && (
            <Alert severity="info" sx={{ py: 0.5, borderRadius: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Ткань 2 используется в {fabricUsage2.sofas.length} диван(ах)
              </Typography>
            </Alert>
          )}
          <Autocomplete
            freeSolo
            readOnly={readOnly}
            options={Array.from(new Set((sofas || []).map(s => s.assignment).filter(Boolean)))}
            value={formData.assignment || ''}
            onChange={(_, v) => change('assignment', v || '')}
            onInputChange={(_, v) => change('assignment', v || '')}
            renderInput={(p) => <TextField {...p} label="Назначение (клиент)" placeholder="Выберите из существующих или введите новое…" />}
            fullWidth
          />
          <Autocomplete
            freeSolo
            readOnly={readOnly}
            options={Array.from(new Set((sofas || []).map(s => s.destination).filter(Boolean)))}
            value={formData.destination || ''}
            onChange={(_, v) => change('destination', v || '')}
            onInputChange={(_, v) => change('destination', v || '')}
            renderInput={(p) => <TextField {...p} label="Место назначения (город)" placeholder="Выберите из существующих или введите новое…" />}
            fullWidth
          />
          <TextField
            label="Дата доставки"
            type="date"
            value={formData.delivery_date}
            onChange={(e) => change('delivery_date', e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{ readOnly }}
            fullWidth
          />
          <TextField
            label="Примечания"
            value={formData.notes}
            onChange={(e) => change('notes', e.target.value)}
            multiline
            rows={3}
            fullWidth
            InputProps={{ readOnly }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{readOnly ? 'Закрыть' : 'Отмена'}</Button>
        {!readOnly && (
          <Button onClick={handleSubmit} variant="contained">
            {mode === 'add' ? 'Создать' : 'Сохранить'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default React.memo(SofaFormDialog);
